/** 判定結果プルダウンで空を表す表示ラベル */
export const EMPTY_RESULT_LABEL = '(空)'

/**
 * 区分項目の値が「(空)」に該当するかどうかを判定する
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export const isEmptyClassificationItemValue = (value) => {
  if (value == null) return true
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  return trimmed === '' || trimmed === EMPTY_RESULT_LABEL
}

/**
 * プルダウン選択肢に合わせて判定結果を正規化する
 *
 * @param {string} current
 * @param {string[]} dropdownOptions
 * @param {boolean} hasEmptyOption
 * @returns {string}
 */
export const normalizeResultForDropdown = (current, dropdownOptions, hasEmptyOption) => {
  if (!dropdownOptions.length) return current ?? ''
  const validSet = new Set(dropdownOptions)
  if (validSet.has(current)) return current
  if (hasEmptyOption) return ''
  return dropdownOptions[0]
}

/**
 * ユーザー区分（DataClassification）に基づく判定結果プルダウン選択肢を算出する
 *
 * @param {string} fieldName
 * @param {{ DataClassificationName?: string; SearchKey?: string }[]} rawDataClassifications
 * @param {{ SearchKey?: string; ItemValue?: string; ItemDisplayOrder?: number }[]} classificationItems
 * @param {string[]} additionalOptions
 * @returns {{
 *   dropdownOptions: string[]
 *   missingClassificationItems: boolean
 *   matched: boolean
 *   hasEmptyOption: boolean
 * }}
 */
const resolveUserClassificationDropdown = (
  fieldName,
  rawDataClassifications,
  classificationItems,
  additionalOptions
) => {
  if (!fieldName || !rawDataClassifications?.length) {
    return { dropdownOptions: [], missingClassificationItems: false, matched: false, hasEmptyOption: false }
  }

  const matchedDataClassification = rawDataClassifications.find((dc) => dc.DataClassificationName === fieldName)

  if (!matchedDataClassification?.SearchKey) {
    return { dropdownOptions: [], missingClassificationItems: false, matched: false, hasEmptyOption: false }
  }

  const codeValue = matchedDataClassification.SearchKey.split('#')[0]
  if (!codeValue) {
    return { dropdownOptions: [], missingClassificationItems: false, matched: false, hasEmptyOption: false }
  }

  const matchedItems = (classificationItems || [])
    .filter((item) => {
      const itemSearchKey = item.SearchKey || ''
      return itemSearchKey.startsWith(`${codeValue}#`)
    })
    .sort((a, b) => {
      const orderA = a.ItemDisplayOrder ?? 9999
      const orderB = b.ItemDisplayOrder ?? 9999
      if (orderA !== orderB) return orderA - orderB
      return (a.ItemValue || '').localeCompare(b.ItemValue || '')
    })

  if (matchedItems.length === 0) {
    return { dropdownOptions: [], missingClassificationItems: true, matched: true, hasEmptyOption: false }
  }

  const rawValues = matchedItems.map((item) => item.ItemValue)
  const hasEmptyOption = rawValues.some(isEmptyClassificationItemValue)

  const uniqueItemValues = []
  const seen = new Set()
  rawValues.forEach((value) => {
    if (isEmptyClassificationItemValue(value) || seen.has(value)) return
    seen.add(value)
    uniqueItemValues.push(value)
  })

  additionalOptions.forEach((result) => {
    if (!isEmptyClassificationItemValue(result) && result && !seen.has(result)) {
      seen.add(result)
      uniqueItemValues.push(result)
    }
  })

  return {
    dropdownOptions: uniqueItemValues,
    missingClassificationItems: false,
    matched: true,
    hasEmptyOption
  }
}

/**
 * CHROFY規定値区分に基づく判定結果プルダウン選択肢を算出する
 *
 * @param {string} fieldName
 * @param {{ ChrofyClassificationName?: string; ChrofyClassificationKey?: string }[]} chrofyClassifications
 * @param {{
 *   ChrofyClassificationKey?: string
 *   ChrofyClassificationItemValue?: string
 *   ChrofyClassificationItemDisplayOrder?: number
 * }[]} chrofyClassificationItems
 * @param {string[]} additionalOptions
 * @returns {{ dropdownOptions: string[]; matched: boolean; hasEmptyOption: boolean }}
 */
const resolveChrofyClassificationDropdown = (
  fieldName,
  chrofyClassifications,
  chrofyClassificationItems,
  additionalOptions
) => {
  if (!fieldName || !chrofyClassifications?.length) {
    return { dropdownOptions: [], matched: false, hasEmptyOption: false }
  }

  const matchedClassification = chrofyClassifications.find((cc) => cc.ChrofyClassificationName === fieldName)

  if (!matchedClassification) {
    return { dropdownOptions: [], matched: false, hasEmptyOption: false }
  }

  const classificationKey = matchedClassification.ChrofyClassificationKey || ''
  if (!classificationKey) {
    return { dropdownOptions: [], matched: true, hasEmptyOption: false }
  }

  const matchedItems = (chrofyClassificationItems || [])
    .filter((item) => (item.ChrofyClassificationKey || '') === classificationKey)
    .sort((a, b) => {
      const orderA = a.ChrofyClassificationItemDisplayOrder ?? 9999
      const orderB = b.ChrofyClassificationItemDisplayOrder ?? 9999
      if (orderA !== orderB) return orderA - orderB
      return (a.ChrofyClassificationItemValue || '').localeCompare(b.ChrofyClassificationItemValue || '')
    })

  const rawValues = matchedItems.map((item) => item.ChrofyClassificationItemValue)
  const hasEmptyOption = rawValues.some(isEmptyClassificationItemValue)

  const uniqueItemValues = []
  const seen = new Set()
  rawValues.forEach((value) => {
    if (isEmptyClassificationItemValue(value) || seen.has(value)) return
    seen.add(value)
    uniqueItemValues.push(value)
  })

  additionalOptions.forEach((result) => {
    if (!isEmptyClassificationItemValue(result) && result && !seen.has(result)) {
      seen.add(result)
      uniqueItemValues.push(result)
    }
  })

  return { dropdownOptions: uniqueItemValues, matched: true, hasEmptyOption }
}

/**
 * データ項目が分類項目かどうかを判定し、判定結果プルダウン用の選択肢を算出する
 *
 * @param {string} fieldName - 選択したデータ項目名（ColumnName）
 * @param {{ DataClassificationName?: string; SearchKey?: string }[]} rawDataClassifications
 * @param {{ SearchKey?: string; ItemValue?: string }[]} classificationItems
 * @param {{ additionalOptions?: string[]; chrofyClassifications?: object[]; chrofyClassificationItems?: object[] }} [options]
 * @returns {{ dropdownOptions: string[]; missingClassificationItems: boolean; hasEmptyOption: boolean }}
 */
export const resolveResultDropdownOptions = (fieldName, rawDataClassifications, classificationItems, options = {}) => {
  const { additionalOptions = [], chrofyClassifications = [], chrofyClassificationItems = [] } = options

  const userResult = resolveUserClassificationDropdown(
    fieldName,
    rawDataClassifications,
    classificationItems,
    additionalOptions
  )
  if (userResult.matched) {
    return {
      dropdownOptions: userResult.dropdownOptions,
      missingClassificationItems: userResult.missingClassificationItems,
      hasEmptyOption: userResult.hasEmptyOption
    }
  }

  const chrofyResult = resolveChrofyClassificationDropdown(
    fieldName,
    chrofyClassifications,
    chrofyClassificationItems,
    additionalOptions
  )
  if (chrofyResult.matched) {
    return {
      dropdownOptions: chrofyResult.dropdownOptions,
      missingClassificationItems: false,
      hasEmptyOption: chrofyResult.hasEmptyOption
    }
  }

  return { dropdownOptions: [], missingClassificationItems: false, hasEmptyOption: false }
}
